import {CertificateInterface, CertificateMethod, SpecialistViewInterface} from '@interfaces/specialist.interface';
import {ModelsInterfaces} from '@namespaces/models.namespace';
import {ObjectsProvider} from '@providers/objects/objects.provider';
import {SpecialistsProvider} from '@providers/specialists/specialists.provider';
import {DateService} from '@services/date.service';
import {WizardInterfaces} from '../wizard.namespace';

export class WizardSpecialist {
    id: number;
    name: string;
    job: string;

    /**
     * Компетенция специалиста.
     * Предсталяет собой коллекцию строк вида 'methodId_objectNumber', на которые умеет специалист
     */
    competence: WizardSpecialistCompetence = {
        methods: [],
        objectsByMethods: []
    };

    /**
     * Номера объектов удостоверений, на которые заявлен специалист.
     */
    pbRawNumbers: string[] = [];

    /**
     * Номера объектов, на которые нет удостоверений ПБ.
     * Используется для отображения во View
     */
    pbDismissedNumbers: string[] = [];

    /**
     * Все номера всех объектов, покрываемые удостоверениями ПБ, включая подобъекты.
     * Используется для рассчета перекрытия удостоверений НК
     */
    pbFullNumbers: string[] = [];

    /**
     * Входит ли специалист в верификацию по заявленной области аттестации или у него другие методы и объекты
     */
    inAttetstion = false;

    methods: NkMethod[] = [];

    constructor(
        private _rawData: SpecialistViewInterface,
        objects: ModelsInterfaces.Object[] = [],
        methods: ModelsInterfaces.Method[] = [],
        attestation: WizardInterfaces.AttestationValue[] = [],
    ) {
        this.setAttributes(objects, methods, attestation);
    }

    get rawData(): SpecialistViewInterface {
        return this._rawData;
    }

    private setAttributes(
        objects: ModelsInterfaces.Object[] = [],
        methods: ModelsInterfaces.Method[] = [],
        attestation: WizardInterfaces.AttestationValue[] = []
    ) {
        this.id = +this.rawData.id;
        this.name = this.rawData.full_name;
        this.job = this.rawData.job_title;

        this.calculatePbNumbers(objects);
        this.calculateMethods(attestation, objects);
        this.calculateCompetence(methods, objects);
        this.calculatePbDismissedNumbers();
    }

    /**
     * Получить номер текущего объекта и всех его подобъектов
     * @param number
     * @param objects
     */
    private getNumbers(number: string, objects: ModelsInterfaces.Object[] = []): string[] {
        const numbers = [];
        objects.some(object => {
            if (object.number === number) {
                numbers.push(...ObjectsProvider.getRootAndChildsNumbers(object));
                return true;
            } else {
                if (object.childs && object.childs.length) {
                    const num = this.getNumbers(number, object.childs);
                    numbers.push(...num);
                    return Boolean(numbers.length);
                }
            }
        });
        return numbers;
    }

    private calculatePbNumbers(objects: ModelsInterfaces.Object[] = []) {
        this.pbRawNumbers = [];
        this.pbFullNumbers = [];
        this.pbDismissedNumbers = [];

        /**
         * Получаем удостоверения ПБ с актуальной датой окончания
         */
        const actualPbCertificated = this.rawData.certificate_pb.filter(certificate => DateService.isDateEndActual(certificate.date_end));

        /**
         * Теперь со всех актуальных удостоверений ПБ собираем уникальные номера объектов
         */
        actualPbCertificated.forEach(pb => {
            const numbers = pb.objects_string.split(', ');
            numbers.forEach(number => {
                if (!this.pbRawNumbers.includes(number)) {
                    this.pbRawNumbers.push(number);
                }
            });
        });

        /**
         * Для всех объектов из удостоверений ПБ добавляем их подобъекты для простоты рассчета
         */
        this.pbRawNumbers.forEach(number => {
            this.pbFullNumbers.push(...this.getNumbers(number, objects));
        });

        this.correctFullNumbers(objects);
    }

    private calculateMethods(attestation: WizardInterfaces.AttestationValue[] = [], objects: ModelsInterfaces.Object[] = []) {
        this.methods = [];
        /**
         * При желании, можно фильтровать удостоверения для получения актуальных даты
         */
        const actualNkCertificated: CertificateInterface[] = this.rawData.certificate_nk;

        /**
         * Из всех актуальных удостоверений достаем только актуальные (не просроченные) записи по методам
         */
        const actualMethods: CertificateMethod[] = actualNkCertificated.reduce((acc, item) => {
            acc.push(...item.methods.filter(m => SpecialistsProvider.isMethodDateActual(m.expiration_date)));
            return acc;
        }, []);

        /**
         * Заполняем методы специалиста, проверяя покрытие объектов удостоверениями ПБ
         */
        this.methods = actualMethods.map(method => {
            const attestationForMethod = attestation.filter(att => {
                return +att.methodId === +method.method_id || att.submethods.map(s => +s.id).includes(+method.method_id);
            });

            const attestationObjectsNumbers: string[] = attestationForMethod.length ? attestationForMethod[0].objects.reduce((acc, obj) => {
                acc.push(...obj.realNumbers);
                return acc;
            }, []) : [];

            /**
             * Метод является присутствующим в аттестации, если любой из его объектов там присутствует
             */
            let isMethodInAttestation = false;

            return {
                id: +method.method_id,
                title: method.short_title,
                level: +method.level,
                objects: method.objects_string.split(', ').map(number => {
                    const numbers = this.getNumbers(number, objects);
                    const isObjectInAttestation = numbers.some(n => attestationObjectsNumbers.includes(n));

                    /**
                     * Метод является присутствующим в аттестации, если любой из его объектов там присутствует
                     */
                    if (isObjectInAttestation) {
                        isMethodInAttestation = true;
                    }
                    return {
                        number: number,
                        valid: this.pbFullNumbers.includes(number),
                        inAttestation: isObjectInAttestation,
                    };
                }),
                inAttestation: isMethodInAttestation,
            };
        });
    }

    /**
     * Вычислить компетенции специалиста.
     * Компетенции представляют собой коллецию всевозможных сочетаний методов и объектов, на которые аттестован специалист
     * Если специалист аттестован на метод, значит он автоматически аттестован на все подметоды
     * Если аттестован на объект, значит автоматически аттестован на все подобъекты этого объекта.
     *
     * Однако обратное не верно, так как мы никогда не сможем гарантировать достоверность утверждений:
     * Если аттестован на все подметоды, это не значит, что аттестован на метод целиком (так как могут быть разные объекты)
     * Если аттестован на все подобъекты, это не значит, что автоматически аттестован на весь объект (могут быть разные методы)
     * @param methods
     * @param objects
     */
    private calculateCompetence(methods: ModelsInterfaces.Method[] = [], objects: ModelsInterfaces.Object[] = []) {
        this.competence.methods = [];
        this.competence.objectsByMethods = [];
        this.methods.forEach(method => {

            const rawMethod = methods.find(m => +m.id === +method.id);
            const methodIds: number[] = [method.id];
            /**
             * Если аттестован на родительский метод, то добавляем и все подметоды тоже
             */
            if (rawMethod && rawMethod.methods && rawMethod.methods.length) {
                methodIds.push(...rawMethod.methods.map(m => +m.id));
            }
            this.competence.methods.push(...methodIds);

            /**
             * В компетенции специаилста пойдут только покрытые удостоверениями ПБ объекты
             */
            method.objects
                .filter(obj => obj.valid)
                .forEach(obj => {
                    /**
                     * Для каждого объекта в компетенции специалиста идут еще и все подобъекты
                     */
                    const numbers = this.getNumbers(obj.number, objects);
                    numbers.forEach(number => {
                        methodIds.forEach(id => {
                            this.competence.objectsByMethods.push(id + '_' + number);
                        });
                    });
                });
        });
    }

    /**
     * Рассчитываем номера объектов, на которые нет ПБ из заявленной области аттестации
     */
    private calculatePbDismissedNumbers() {
        this.pbDismissedNumbers = this.methods.reduce((acc, method) => {
            acc.push(...method.objects.filter(obj => !obj.valid && obj.inAttestation && !acc.includes(obj.number)).map(obj => obj.number));
            return acc;
        }, []).sort();
        this.inAttetstion = this.methods.some(m => m.inAttestation);
    }

    /**
     * Корректируем номера объектов удостоверений ПБ.
     * Так если у специалиста несколько ПБ на подобъекты, которые суммарно покрывают объект, то сам объект тоже будем читать из его области
     * @param objects
     */
    private correctFullNumbers(objects: ModelsInterfaces.Object[] = []) {
        /**
         * Сначала пропускаем все объекты, которые уже полностью покрыты удостоверениями
         */
        objects
            .filter(obj => !this.pbFullNumbers.includes(obj.number))
            .forEach(obj => {
                /**
                 * Для каждого объектв берем номера только его подобъектов и, если все эти подобъекты уже есть в удостоверениях,
                 * то считаем, что весь объект тоже есть.
                 */
                const childNumbers = ObjectsProvider.getOnlyChildsNumbers(obj);
                if (!childNumbers.length) {
                    return;
                }
                const isFullByParts = childNumbers.every(number => this.pbFullNumbers.includes(number));

                if (isFullByParts) {
                    this.pbFullNumbers.push(obj.number);
                    /**
                     * Для вложенностей более 1 (например для объекта 2, когда есть 2, 2.1, 2.1.1)
                     * может возникнуть ситуация, что подобъектами покрыто, но промежуточных не хватает
                     * Например, у нас было наличие 2.1.1, 2.1.2, 2.2, 2.3, значит покрыт объект 2.1, а это значит что весь 2 тоже покрыт,
                     * но из алгоритма выше мы добавим только объект 2, не добавив 2.1.
                     * Код ниже добавит все недостающие промежуточные
                     */
                    const fullNumbers = ObjectsProvider.getRootAndChildsNumbers(obj);
                    this.pbFullNumbers.push(...fullNumbers.filter(number => !this.pbFullNumbers.includes(number)));
                }
            });
    }
}

export interface WizardSpecialistCompetence {
    methods: number[];
    objectsByMethods: string[];
}

interface NkMethod {
    id: number;
    title: string;
    level: number;
    inAttestation: boolean;
    objects: {
        number: string;
        valid: boolean;
        /**
         * Заявлен ли текущий объект в области аттестации
         */
        inAttestation: boolean;
    }[];
}
